import { Route } from '@angular/router';
import { initialDataResolver } from './app.resolvers';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { NoAuthGuard } from './core/auth/guards/noAuth.guard';

import { languageGuard } from '@hmax-order/frontend/utils';

import { RoleGuard } from './core/auth/guards/role.guard';

// @formatter:off

export const appRoutes: Route[] = [
  // Redirecionar path vazio para uma rota padrão, que necessita de autenticação
  { path: '', pathMatch: 'full', redirectTo: 'home' },

  // Depois de o usuário logar, a tela de login redirecionará o usuário para o path 'signed-in-redirect',
  // que fará o redirecionamento para o local desejado
  // Este é uma pequena conveniência para manter todas as rotas juntas neste arquivo.
  { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'home' },

  // Rotas para usuários não autenticados
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    loadComponent: () => import('./layout/layout.component').then((m) => m.LayoutComponent),
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'confirmation-required',
        canActivate: [languageGuard],
        loadChildren: () => import('./modules/auth/confirmation-required/confirmation-required.routes'),
      },
      {
        path: 'forgot-password',
        canActivate: [languageGuard],
        loadChildren: () => import('./modules/auth/forgot-password/forgot-password.routes'),
      },
      {
        path: 'reset-password',
        canActivate: [languageGuard],
        loadChildren: () => import('./modules/auth/reset-password/reset-password.routes'),
      },
      { path: 'sign-in', canActivate: [languageGuard], loadChildren: () => import('./modules/auth/sign-in/sign-in.routes') },
      { path: 'sign-up', loadChildren: () => import('./modules/auth/sign-up/sign-up.routes') },
    ],
  },

  // Rotas para usuários autenticados

  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    loadComponent: () => import('./layout/layout.component').then((m) => m.LayoutComponent),
    data: {
      layout: 'empty',
    },
    children: [
      { path: 'sign-out', loadChildren: () => import('./modules/auth/sign-out/sign-out.routes') },
      { path: 'unlock-session', loadChildren: () => import('./modules/auth/unlock-session/unlock-session.routes') },
      // {
      //   path: 'sign-out',
      //   loadChildren: () => import('./modules/auth/sign-out/sign-out.module').then((m) => m.AuthSignOutModule),
      // },
      // {
      //   path: 'unlock-session',
      //   loadChildren: () => import('./modules/auth/unlock-session/unlock-session.module').then((m) => m.AuthUnlockSessionModule),
      // },
    ],
  },

  // A "Landing Module" do template Fuse foi REMOVIDA daqui em 2026-08-13: ela registrava `home`
  // público ANTES do bloco admin, então o redirect raiz caía na landing e o usuário precisava
  // clicar "Launch the App" para chegar ao painel. O produto não tem landing pública — deslogado
  // vai para o sign-in, logado cai na tela "Início". Os arquivos de `modules/landing` ficam órfãos
  // (poda de código morto do template é passada própria, junto com o `example`).

  // Cardápio público (F4/D1): /menu/:slug — SEM AuthGuard/NoAuthGuard.
  // Precede o bloco admin porque o `**` de lá capturaria /menu/:slug e exigiria login.
  //
  // F5: usa o `EmptyLayoutComponent` DIRETO, não o `LayoutComponent`. O `LayoutComponent` é um
  // switcher que importa estaticamente os 11 layouts do Fuse (navegação, user menu, busca,
  // notificações, quick-chat) para renderizar um só — o hóspede baixaria tudo isso para ver um
  // cardápio. O `Empty` é uma barra de carregamento e um `router-outlet`.
  //
  // Contrapartida assumida: o `LayoutComponent` é quem aplica tema/scheme no `<body>`, então esta
  // rota não tem os utilitários de tema do Fuse (`bg-primary` e afins). É o correto aqui — o
  // cardápio usa as cores do ESTABELECIMENTO (cfgs `COMPANY_*`), não a marca da plataforma.
  {
    path: 'menu',
    loadComponent: () => import('./layout/layouts/empty/empty.component').then((m) => m.EmptyLayoutComponent),
    loadChildren: () => import('@hmax-order/frontend/menu').then((r) => r.menuRoutes),
  },

  // Documentação da API de integração: /docs — SEM AuthGuard/NoAuthGuard, pelo mesmo motivo do
  // cardápio e alinhada à decisão que tornou a rota do backend pública (F12.0/D159): quem integra
  // precisa abrir a documentação sem conta na plataforma.
  //
  // A página é renderizada AQUI, no endereço do painel; não redireciona mais para o domínio da API.
  //
  // Também precede o bloco admin, cujo `**` capturaria /docs e mandaria o visitante para o login.
  {
    path: 'docs',
    loadComponent: () => import('./modules/docs/docs.component').then((m) => m.DocsComponent),
  },

  // Admin routes
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    loadComponent: () => import('./layout/layout.component').then((m) => m.LayoutComponent),
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      { path: 'example', loadChildren: () => import('./modules/admin/example/example.routes') },
      // Tela "Início" — dashboard de boas-vindas (2026-08-13). É o destino do login e do redirect raiz.
      { path: 'home', loadChildren: () => import('@hmax-order/frontend/home').then((r) => r.homeRoutes) },

      { path: 'me', loadChildren: () => import('@hmax-order/frontend/user-cfg').then((m) => m.UserCfgModule) },

      { path: 'users', loadChildren: () => import('@hmax-order/frontend/user').then((r) => r.userRoutes) },
      { path: 'products', loadChildren: () => import('@hmax-order/frontend/product').then((r) => r.productRoutes) },
      { path: 'points-of-sale', loadChildren: () => import('@hmax-order/frontend/point-of-sale').then((r) => r.pointOfSaleRoutes) },
      { path: 'locations', loadChildren: () => import('@hmax-order/frontend/location').then((r) => r.locationRoutes) },
      { path: 'menu-preview', loadChildren: () => import('@hmax-order/frontend/menu-preview').then((r) => r.menuPreviewRoutes) },
      // F7 — painel de acompanhamento. Sem `RoleGuard`: o perfil BASIC (o operador do salão) tem de
      // entrar, e o item de menu `orders` já existia em `company-nav-data.ts` apontando para cá.
      { path: 'orders', loadChildren: () => import('@hmax-order/frontend/order').then((r) => r.orderRoutes) },
      // F13 — administração de contas. Sem `RoleGuard` pela mesma razão do painel (D178): o caixa
      // que abre, fecha e transfere conta é o mesmo BASIC que move a fila de pedidos.
      { path: 'accounts', loadChildren: () => import('@hmax-order/frontend/pdv-account').then((r) => r.pdvAccountRoutes) },
      { path: 'cfg', loadChildren: () => import('@hmax-order/frontend/cfg').then((m) => m.CfgModule) },

      {
        path: 'companies',
        canActivate: [RoleGuard],
        data: { roles: ['SUPER'] },
        loadChildren: () => import('@hmax-order/frontend/company').then((r) => r.companyRoutes),
      },
      {
        path: 'organizations',
        canActivate: [RoleGuard],
        data: { roles: ['SUPER'] },
        loadChildren: () => import('@hmax-order/frontend/company').then((r) => r.organizationRoutes),
      },
      {
        path: 'integrators',
        canActivate: [RoleGuard],
        data: { roles: ['SUPER'] },
        loadComponent: () => import('@hmax-order/frontend/integrator').then((c) => c.IntegratorListComponent),
      },
      {
        path: 'integrations',
        canActivate: [RoleGuard],
        data: { roles: ['SUPER'] },
        loadComponent: () => import('@hmax-order/frontend/integration').then((c) => c.IntegrationListComponent),
      },

      // Sempre deve ser a ultima rota do arquivo, não alterar
      { path: '**', pathMatch: 'full', redirectTo: 'home' },
    ],
  },
];
